import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { insightsApi } from '../lib/api';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Loader2 } from 'lucide-react';

const ETB = (n) => `ETB ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const SEV = { HIGH: 'bg-red-100 text-red-700 border-red-200', MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200', LOW: 'bg-slate-100 text-slate-600 border-slate-200' };

/**
 * No-AI insights: statistical forecast + rule-based recommendations.
 * Embedded as a tab inside the Analytics page.
 */
export default function InsightsPanel({ propertyId }) {
  const [metric, setMetric] = useState('revenue');
  const [forecast, setForecast] = useState(null);
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const [f, r] = await Promise.all([insightsApi.forecast(propertyId, metric, 14), insightsApi.recommendations(propertyId)]);
      setForecast(f.data); setRecs(r.data);
    } catch (e) { toast.error('Failed to load insights'); }
    finally { setLoading(false); }
  }, [propertyId, metric]);
  useEffect(() => { load(); }, [load]);

  const hist = forecast?.history || [];
  const fc = forecast?.forecast || [];
  const allVals = [...hist.map((h) => h.value), ...fc.map((f) => f.value)];
  const maxV = Math.max(...allVals, 1);
  const fmt = (v) => (metric === 'occupancy' ? `${Number(v).toFixed(0)}%` : ETB(v));
  const TrendIcon = forecast?.trend === 'up' ? TrendingUp : forecast?.trend === 'down' ? TrendingDown : Minus;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><TrendIcon className="h-4 w-4" /> {metric === 'occupancy' ? 'Occupancy' : 'Revenue'} Forecast (next 14 days)</CardTitle>
            <CardDescription>Moving average + trend projection (no AI)</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={metric === 'revenue' ? 'default' : 'outline'} onClick={() => setMetric('revenue')}>Revenue</Button>
            <Button size="sm" variant={metric === 'occupancy' ? 'default' : 'outline'} onClick={() => setMetric('occupancy')}>Occupancy</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            : (hist.length + fc.length === 0) ? <p className="text-sm text-muted-foreground py-6 text-center">{forecast?.note || 'No data to forecast yet.'}</p>
            : (
              <div>
                <div className="flex items-end gap-1 h-40 overflow-x-auto">
                  {hist.map((h, i) => (
                    <div key={`h${i}`} className="flex flex-col items-center justify-end flex-1 min-w-[8px]" title={`${h.date}: ${fmt(h.value)}`}>
                      <div className="w-full bg-slate-300 rounded-t" style={{ height: `${Math.max((h.value / maxV) * 100, 2)}%` }} />
                    </div>
                  ))}
                  {fc.map((f, i) => (
                    <div key={`f${i}`} className="flex flex-col items-center justify-end flex-1 min-w-[8px]" title={`${f.date} (forecast): ${fmt(f.value)}`}>
                      <div className="w-full bg-violet-500 rounded-t" style={{ height: `${Math.max((f.value / maxV) * 100, 2)}%` }} />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-slate-300 rounded" /> History</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-violet-500 rounded" /> Forecast</span>
                  {forecast?.average != null && <span className="ml-auto">Avg: <b>{fmt(forecast.average)}</b></span>}
                  {fc.length > 0 && <span>Projected end: <b>{fmt(fc[fc.length - 1].value)}</b></span>}
                </div>
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Recommendations</CardTitle>
          <CardDescription>
            {recs ? `${recs.by_severity?.HIGH || 0} high · ${recs.by_severity?.MEDIUM || 0} medium · ${recs.by_severity?.LOW || 0} low` : '—'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            : (recs?.recommendations || []).length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">All clear — no action items right now. 🎉</p>
            : recs.recommendations.map((r, i) => (
              <div key={i} className={`rounded-lg border p-3 flex items-start gap-3 ${SEV[r.severity]}`}>
                <Badge className={SEV[r.severity]} variant="outline">{r.severity}</Badge>
                <div className="min-w-0">
                  <p className="font-medium text-sm text-slate-900">{r.title} <span className="text-xs font-normal text-slate-500">· {r.category}</span></p>
                  <p className="text-xs text-slate-600">{r.detail}</p>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}
